/**
 * Armadillo Adamantium Next — Behavioral Biometrics Engine
 *
 * Continuous passive authentication via behavioral analysis.
 * Collects keystroke dynamics, mouse movements, scroll patterns,
 * and touch pressure. Sends metrics to server for profile comparison.
 *
 * This replaces the client-side-only tracking with real
 * server-side behavioral profiling.
 */

import { supabase } from '@/integrations/supabase/client';
import { sha256 } from 'js-sha256';

// ============================================
// TYPES
// ============================================

export interface KeystrokeMetrics {
  avgDwellTimeMs: number;
  avgFlightTimeMs: number;
  avgKeyIntervalMs: number;
  typingSpeedWpm: number;
  commonDigraphTimings: Record<string, number>;
  sampleCount: number;
}

export interface MouseMetrics {
  avgVelocity: number;
  avgAcceleration: number;
  avgJerk: number;
  pathComplexity: number;
  clickPatterns: number[];
  sampleCount: number;
}

export interface ScrollMetrics {
  avgScrollVelocity: number;
  scrollDirectionChanges: number;
  scrollBurstFrequency: number;
  sampleCount: number;
}

export interface TouchMetrics {
  avgTouchPressure: number;
  avgTouchDuration: number;
  touchPatterns: string[];
  sampleCount: number;
}

export interface BehavioralProfile {
  keystroke: KeystrokeMetrics;
  mouse: MouseMetrics;
  scroll: ScrollMetrics;
  touch: TouchMetrics;
  behaviorScore: number;
  isCalibrated: boolean;
  confidence: number;
}

// ============================================
// KEYSTROKE DYNAMICS COLLECTOR
// ============================================

class KeystrokeCollector {
  private keyDownTimes: Map<string, number> = new Map();
  private keyUpTimes: Map<string, number> = new Map();
  private flightTimes: number[] = [];
  private dwellTimes: number[] = [];
  private digraphTimings: Map<string, number[]> = new Map();
  private lastKeyUpTime = 0;
  private charCount = 0;
  private startTime = Date.now();

  private boundKeyDown: ((e: KeyboardEvent) => void) | null = null;
  private boundKeyUp: ((e: KeyboardEvent) => void) | null = null;

  start() {
    this.boundKeyDown = (e: KeyboardEvent) => {
      const now = performance.now();
      this.keyDownTimes.set(e.key, now);

      // Track flight time (time between previous key up and this key down)
      if (this.lastKeyUpTime > 0) {
        const flight = now - this.lastKeyUpTime;
        if (flight > 0 && flight < 5000) {
          this.flightTimes.push(flight);
          if (this.flightTimes.length > 200) this.flightTimes.shift();
        }
      }
    };

    this.boundKeyUp = (e: KeyboardEvent) => {
      const now = performance.now();
      const downTime = this.keyDownTimes.get(e.key);

      if (downTime !== undefined) {
        const dwell = now - downTime;
        if (dwell > 0 && dwell < 2000) {
          this.dwellTimes.push(dwell);
          if (this.dwellTimes.length > 200) this.dwellTimes.shift();
        }

        // Track digraph (two-key sequence) timing
        const keys = Array.from(this.keyUpTimes.entries());
        if (keys.length > 0) {
          const [prevKey, prevUp] = keys[keys.length - 1];
          const digraphKey = prevKey + e.key;
          const digraphTime = now - prevUp;

          if (digraphTime > 0 && digraphTime < 3000) {
            const existing = this.digraphTimings.get(digraphKey) || [];
            existing.push(digraphTime);
            if (existing.length > 20) existing.shift();
            this.digraphTimings.set(digraphKey, existing);
          }
        }

        this.keyUpTimes.set(e.key, now);
        this.lastKeyUpTime = now;
        this.charCount++;
      }

      this.keyDownTimes.delete(e.key);
    };

    document.addEventListener('keydown', this.boundKeyDown, true);
    document.addEventListener('keyup', this.boundKeyUp, true);
  }

  stop() {
    if (this.boundKeyDown) document.removeEventListener('keydown', this.boundKeyDown, true);
    if (this.boundKeyUp) document.removeEventListener('keyup', this.boundKeyUp, true);
  }

  getMetrics(): KeystrokeMetrics {
    const avg = (arr: number[]) => arr.length > 0 ? arr.reduce((a, b) => a + b, 0) / arr.length : 0;

    // Compute common digraph averages
    const commonDigraphs: Record<string, number> = {};
    for (const [key, times] of this.digraphTimings) {
      if (times.length >= 3) {
        commonDigraphs[key] = avg(times);
      }
    }

    const elapsed = (Date.now() - this.startTime) / 60000; // minutes
    const wpm = elapsed > 0 ? this.charCount / (elapsed * 5) : 0; // 5 chars = 1 word

    return {
      avgDwellTimeMs: Math.round(avg(this.dwellTimes) * 100) / 100,
      avgFlightTimeMs: Math.round(avg(this.flightTimes) * 100) / 100,
      avgKeyIntervalMs: Math.round(avg([...this.flightTimes, ...this.dwellTimes]) * 100) / 100,
      typingSpeedWpm: Math.round(wpm),
      commonDigraphTimings: commonDigraphs,
      sampleCount: this.dwellTimes.length,
    };
  }

  reset() {
    this.dwellTimes = [];
    this.flightTimes = [];
    this.digraphTimings.clear();
    this.keyDownTimes.clear();
    this.keyUpTimes.clear();
    this.charCount = 0;
    this.startTime = Date.now();
  }
}

// ============================================
// MOUSE DYNAMICS COLLECTOR
// ============================================

class MouseCollector {
  private positions: { x: number; y: number; t: number }[] = [];
  private velocities: number[] = [];
  private accelerations: number[] = [];
  private jerks: number[] = [];
  private clickTimes: number[] = [];
  private boundMouseMove: ((e: MouseEvent) => void) | null = null;
  private boundClick: ((e: MouseEvent) => void) | null = null;

  start() {
    let lastPos = { x: 0, y: 0, t: 0 };
    let lastVel = 0;
    let lastAccel = 0;

    this.boundMouseMove = (e: MouseEvent) => {
      const now = performance.now();
      const dt = now - lastPos.t;

      if (dt > 0 && dt < 1000) {
        const dx = e.clientX - lastPos.x;
        const dy = e.clientY - lastPos.y;
        const dist = Math.sqrt(dx * dx + dy * dy);

        // Velocity (pixels per ms)
        const vel = dist / dt;
        this.velocities.push(vel);
        if (this.velocities.length > 300) this.velocities.shift();

        // Acceleration
        const accel = Math.abs(vel - lastVel) / dt;
        this.accelerations.push(accel);
        if (this.accelerations.length > 300) this.accelerations.shift();

        // Jerk (change in acceleration)
        const jerk = Math.abs(accel - lastAccel) / dt;
        this.jerks.push(jerk);
        if (this.jerks.length > 300) this.jerks.shift();

        lastVel = vel;
        lastAccel = accel;
      }

      this.positions.push({ x: e.clientX, y: e.clientY, t: now });
      if (this.positions.length > 500) this.positions.shift();

      lastPos = { x: e.clientX, y: e.clientY, t: now };
    };

    this.boundClick = () => {
      this.clickTimes.push(performance.now());
      if (this.clickTimes.length > 50) this.clickTimes.shift();
    };

    document.addEventListener('mousemove', this.boundMouseMove, { passive: true });
    document.addEventListener('click', this.boundClick, { passive: true });
  }

  stop() {
    if (this.boundMouseMove) document.removeEventListener('mousemove', this.boundMouseMove);
    if (this.boundClick) document.removeEventListener('click', this.boundClick);
  }

  getMetrics(): MouseMetrics {
    const avg = (arr: number[]) => arr.length > 0 ? arr.reduce((a, b) => a + b, 0) / arr.length : 0;

    // Path complexity: number of direction changes
    let directionChanges = 0;
    for (let i = 2; i < this.positions.length; i++) {
      const dx1 = this.positions[i].x - this.positions[i - 1].x;
      const dx2 = this.positions[i - 1].x - this.positions[i - 2].x;
      const dy1 = this.positions[i].y - this.positions[i - 1].y;
      const dy2 = this.positions[i - 1].y - this.positions[i - 2].y;

      if ((dx1 * dx2 < 0) || (dy1 * dy2 < 0)) {
        directionChanges++;
      }
    }

    // Click timing pattern
    const clickIntervals: number[] = [];
    for (let i = 1; i < this.clickTimes.length; i++) {
      clickIntervals.push(this.clickTimes[i] - this.clickTimes[i - 1]);
    }

    return {
      avgVelocity: Math.round(avg(this.velocities) * 1000) / 1000,
      avgAcceleration: Math.round(avg(this.accelerations) * 10000) / 10000,
      avgJerk: Math.round(avg(this.jerks) * 10000) / 10000,
      pathComplexity: directionChanges,
      clickPatterns: clickIntervals.slice(-10),
      sampleCount: this.velocities.length,
    };
  }

  reset() {
    this.positions = [];
    this.velocities = [];
    this.accelerations = [];
    this.jerks = [];
    this.clickTimes = [];
  }
}

// ============================================
// SCROLL & TOUCH COLLECTOR
// ============================================

class ScrollTouchCollector {
  private scrollVelocities: number[] = [];
  private scrollDirectionChanges = 0;
  private scrollBursts = 0;
  private touchPressures: number[] = [];
  private touchDurations: number[] = [];
  private touchStart = 0;
  private lastScrollDir = 0;
  private boundScroll: ((e: Event) => void) | null = null;
  private boundTouchStart: ((e: TouchEvent) => void) | null = null;
  private boundTouchEnd: ((e: TouchEvent) => void) | null = null;

  start() {
    this.boundScroll = (e: Event) => {
      const target = e.target as Element;
      if (!target || !('scrollTop' in target)) return;

      const scrollTop = (target as HTMLElement).scrollTop || 0;
      const now = performance.now();

      // Approximate scroll velocity from event
      // (We don't track absolute position, so use scroll event frequency as proxy)
      this.scrollVelocities.push(now);
      if (this.scrollVelocities.length > 200) this.scrollVelocities.shift();

      // Direction detection
      const wheelEvent = e as WheelEvent;
      if (wheelEvent.deltaY) {
        const dir = wheelEvent.deltaY > 0 ? 1 : -1;
        if (this.lastScrollDir !== 0 && dir !== this.lastScrollDir) {
          this.scrollDirectionChanges++;
        }
        this.lastScrollDir = dir;
      }

      // Burst detection (rapid scroll events)
      if (this.scrollVelocities.length >= 2) {
        const lastTwo = this.scrollVelocities.slice(-2);
        if (lastTwo[1] - lastTwo[0] < 50) {
          this.scrollBursts++;
        }
      }
    };

    this.boundTouchStart = (e: TouchEvent) => {
      if (e.touches.length > 0) {
        const touch = e.touches[0];
        this.touchPressures.push(touch.force || 0.5);
        if (this.touchPressures.length > 100) this.touchPressures.shift();
        this.touchStart = performance.now();
      }
    };

    this.boundTouchEnd = () => {
      if (this.touchStart > 0) {
        const duration = performance.now() - this.touchStart;
        this.touchDurations.push(duration);
        if (this.touchDurations.length > 100) this.touchDurations.shift();
        this.touchStart = 0;
      }
    };

    document.addEventListener('scroll', this.boundScroll, { passive: true });
    document.addEventListener('touchstart', this.boundTouchStart, { passive: true });
    document.addEventListener('touchend', this.boundTouchEnd, { passive: true });
  }

  stop() {
    if (this.boundScroll) document.removeEventListener('scroll', this.boundScroll);
    if (this.boundTouchStart) document.removeEventListener('touchstart', this.boundTouchStart);
    if (this.boundTouchEnd) document.removeEventListener('touchend', this.boundTouchEnd);
  }

  getMetrics(): { scroll: ScrollMetrics; touch: TouchMetrics } {
    const avg = (arr: number[]) => arr.length > 0 ? arr.reduce((a, b) => a + b, 0) / arr.length : 0;

    // Scroll velocity: events per second
    const scrollWindow = this.scrollVelocities.slice(-60);
    const scrollDuration = scrollWindow.length > 1
      ? (scrollWindow[scrollWindow.length - 1] - scrollWindow[0]) / 1000
      : 1;
    const scrollVelocity = scrollWindow.length / Math.max(scrollDuration, 0.1);

    return {
      scroll: {
        avgScrollVelocity: Math.round(scrollVelocity * 100) / 100,
        scrollDirectionChanges: this.scrollDirectionChanges,
        scrollBurstFrequency: this.scrollBursts,
        sampleCount: this.scrollVelocities.length,
      },
      touch: {
        avgTouchPressure: Math.round(avg(this.touchPressures) * 1000) / 1000,
        avgTouchDuration: Math.round(avg(this.touchDurations)),
        touchPatterns: [],
        sampleCount: this.touchDurations.length,
      },
    };
  }

  reset() {
    this.scrollVelocities = [];
    this.scrollDirectionChanges = 0;
    this.scrollBursts = 0;
    this.touchPressures = [];
    this.touchDurations = [];
  }
}

// ============================================
// BEHAVIORAL BIOMETRICS ENGINE
// ============================================

class BehavioralBiometricsEngine {
  private static instance: BehavioralBiometricsEngine;
  private keystroke = new KeystrokeCollector();
  private mouse = new MouseCollector();
  private scrollTouch = new ScrollTouchCollector();
  private isRunning = false;
  private profile: BehavioralProfile | null = null;
  private syncInterval: ReturnType<typeof setInterval> | null = null;
  private lastSync = 0;
  private readonly SYNC_INTERVAL_MS = 30000; // Sync every 30 seconds

  static getInstance(): BehavioralBiometricsEngine {
    if (!BehavioralBiometricsEngine.instance) {
      BehavioralBiometricsEngine.instance = new BehavioralBiometricsEngine();
    }
    return BehavioralBiometricsEngine.instance;
  }

  /**
   * Start collecting behavioral data
   */
  start() {
    if (this.isRunning) return;

    this.keystroke.start();
    this.mouse.start();
    this.scrollTouch.start();

    // Periodic sync to server
    this.syncInterval = setInterval(() => this.syncToServer(), this.SYNC_INTERVAL_MS);

    this.isRunning = true;

  }

  /**
   * Stop collecting
   */
  stop() {
    if (!this.isRunning) return;

    this.keystroke.stop();
    this.mouse.stop();
    this.scrollTouch.stop();

    if (this.syncInterval) {
      clearInterval(this.syncInterval);
      this.syncInterval = null;
    }

    this.isRunning = false;
  }

  /**
   * Get current behavioral profile
   */
  getProfile(): BehavioralProfile {
    const keystroke = this.keystroke.getMetrics();
    const mouse = this.mouse.getMetrics();
    const { scroll, touch } = this.scrollTouch.getMetrics();

    // Compute behavior score: higher = more human-like
    let score = 0.5;

    // Keystroke scoring
    if (keystroke.sampleCount > 10) {
      // Normal dwell time: 50-300ms
      if (keystroke.avgDwellTimeMs >= 50 && keystroke.avgDwellTimeMs <= 300) score += 0.1;
      // Normal flight time: 50-500ms
      if (keystroke.avgFlightTimeMs >= 50 && keystroke.avgFlightTimeMs <= 500) score += 0.1;
    }

    // Mouse scoring
    if (mouse.sampleCount > 20) {
      // Humans have non-zero jerk (micro-corrections)
      if (mouse.avgJerk > 0) score += 0.1;
      // Path complexity (direction changes are human)
      if (mouse.pathComplexity > 5) score += 0.05;
    }

    this.profile = {
      keystroke,
      mouse,
      scroll,
      touch,
      behaviorScore: Math.min(1.0, Math.max(0.0, score)),
      isCalibrated: keystroke.sampleCount > 20 && mouse.sampleCount > 30,
      confidence: Math.min(1.0, (keystroke.sampleCount + mouse.sampleCount) / 200),
    };

    return this.profile;
  }

  /**
   * Get behavior score for risk computation
   */
  getBehaviorScore(): number {
    const profile = this.getProfile();
    return profile.behaviorScore;
  }

  /**
   * Sync behavioral data to server
   */
  async syncToServer(): Promise<void> {
    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) return;

      const profile = this.getProfile();
      if (!profile.isCalibrated) return;

      const metrics = {
        avg_dwell_time_ms: profile.keystroke.avgDwellTimeMs,
        avg_flight_time_ms: profile.keystroke.avgFlightTimeMs,
        avg_key_interval_ms: profile.keystroke.avgKeyIntervalMs,
        typing_speed_wpm: profile.keystroke.typingSpeedWpm,
        avg_mouse_velocity: profile.mouse.avgVelocity,
        avg_mouse_acceleration: profile.mouse.avgAcceleration,
        avg_mouse_jerk: profile.mouse.avgJerk,
        mouse_path_complexity: profile.mouse.pathComplexity,
        avg_scroll_velocity: profile.scroll.avgScrollVelocity,
        scroll_direction_changes: profile.scroll.scrollDirectionChanges,
      };

      const { data } = await (supabase.rpc as any)('update_behavioral_profile', {
        p_user_id: user.id,
        p_metrics: metrics,
      });

      if (data) {

        this.lastSync = Date.now();
      }
    } catch (error) {

    }
  }

  /**
   * Get metrics for security context (sent with each request)
   */
  getSecurityMetrics(): {
    behavior_score: number;
    behavior_metrics: Record<string, number>;
  } {
    const profile = this.getProfile();
    return {
      behavior_score: profile.behaviorScore,
      behavior_metrics: {
        dwell: profile.keystroke.avgDwellTimeMs,
        flight: profile.keystroke.avgFlightTimeMs,
        velocity: profile.mouse.avgVelocity,
        jerk: profile.mouse.avgJerk,
        scroll_vel: profile.scroll.avgScrollVelocity,
      },
    };
  }
}

export const behavioralBiometrics = BehavioralBiometricsEngine.getInstance();
export type { BehavioralProfile as BehavioralProfileData };

// ============================================
// POLISH: ENROLLMENT BASELINES & DRIFT HANDLING
// ============================================

import { BehavioralBaseline, KeystrokeProfile, MouseProfile } from './types';

/**
 * In-memory enrollment baselines for quick comparison
 */
const enrollmentBaselines = new Map<string, BehavioralBaseline>();
const DRIFT_THRESHOLD = 0.15; // 15% deviation before drift adjustment
const MAX_DRIFT_FACTOR = 0.5; // Maximum drift allowed
const BASELINE_MIN_SAMPLES = 20; // Minimum samples before baseline is confident

/**
 * Enroll a new behavioral baseline for a user
 * Call this after the user has been active for a while (e.g., 5 minutes)
 */
export function enrollBehavioralBaseline(
  userId: string,
  keystroke: KeystrokeProfile,
  mouse: MouseProfile
): BehavioralBaseline {
  const baseline: BehavioralBaseline = {
    userId,
    keystrokeProfile: keystroke,
    mouseProfile: mouse,
    enrolledAt: Date.now(),
    lastUpdated: Date.now(),
    confidence: Math.min(1, (keystroke.sampleCount + mouse.sampleCount) / (BASELINE_MIN_SAMPLES * 2)),
    driftFactor: 0,
  };
  
  enrollmentBaselines.set(userId, baseline);
  return baseline;
}

/**
 * Compare current behavior against enrolled baseline
 * Returns deviation score (0=identical, 1=completely different)
 */
export function compareAgainstBaseline(
  userId: string,
  currentDwellMs: number,
  currentFlightMs: number,
  currentVelocity: number
): { deviation: number; drifted: boolean; confidence: number } {
  const baseline = enrollmentBaselines.get(userId);
  if (!baseline || baseline.confidence < 0.5) {
    return { deviation: 0.5, drifted: false, confidence: 0 }; // No baseline yet
  }
  
  // Compute deviation for each metric
  const dwellDeviation = Math.abs(currentDwellMs - baseline.keystrokeProfile.avgDwellMs) /
    Math.max(1, baseline.keystrokeProfile.stdDevDwellMs);
  const flightDeviation = Math.abs(currentFlightMs - baseline.keystrokeProfile.avgFlightMs) /
    Math.max(1, baseline.keystrokeProfile.stdDevFlightMs);
  const velocityDeviation = Math.abs(currentVelocity - baseline.mouseProfile.avgVelocity) /
    Math.max(1, baseline.mouseProfile.stdDevVelocity);
  
  // Composite deviation (average of z-scores, normalized to 0-1)
  const compositeDeviation = Math.min(1, (dwellDeviation + flightDeviation + velocityDeviation) / 9);
  
  // Check if drift adjustment is needed
  const drifted = compositeDeviation > DRIFT_THRESHOLD + baseline.driftFactor;
  
  return {
    deviation: compositeDeviation,
    drifted,
    confidence: baseline.confidence,
  };
}

/**
 * Adjust baseline for natural behavioral drift
 * Human behavior changes over time (fatigue, new keyboard, etc.)
 */
export function adjustBaselineForDrift(
  userId: string,
  newDwellMs: number,
  newFlightMs: number,
  newVelocity: number
): boolean {
  const baseline = enrollmentBaselines.get(userId);
  if (!baseline) return false;
  
  // Only adjust if we have enough confidence
  if (baseline.confidence < 0.5) return false;
  
  // Gradual drift: move baseline 10% toward new observation
  const driftRate = 0.1;
  
  baseline.keystrokeProfile.avgDwellMs += (newDwellMs - baseline.keystrokeProfile.avgDwellMs) * driftRate;
  baseline.keystrokeProfile.avgFlightMs += (newFlightMs - baseline.keystrokeProfile.avgFlightMs) * driftRate;
  baseline.mouseProfile.avgVelocity += (newVelocity - baseline.mouseProfile.avgVelocity) * driftRate;
  
  // Cap drift factor
  baseline.driftFactor = Math.min(MAX_DRIFT_FACTOR, baseline.driftFactor + 0.01);
  baseline.lastUpdated = Date.now();
  
  return true;
}

/**
 * Compute confidence interval for a behavioral metric
 * Returns [lower, upper] bounds at 95% confidence
 */
export function computeConfidenceInterval(
  mean: number,
  stdDev: number,
  sampleCount: number
): [number, number] {
  if (sampleCount < 2) return [mean, mean];
  // 95% CI: mean ± 1.96 * (stdDev / sqrt(n))
  const margin = 1.96 * (stdDev / Math.sqrt(sampleCount));
  return [mean - margin, mean + margin];
}
